import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { Partner } from '../../partner/models/partner';
import { ScanCard } from '../../vu/models/scan-card';
import { ScanPartnerCodeInfo } from '../models/scan-partner-code-info';
import { PartnerCodeService } from './partner-code.service';

@Injectable()
export class PartnerCodeStateService {

  private _partner: Partner;
  private _partnerCode: string;
  private _pin: string;
  scanPartnerCodeInfo: ScanPartnerCodeInfo;
  authorizationChanged = new Subject();
  public activateMode = false;

  constructor(
    private shopSettingsService: ShopSettingsService,
    private partnerCodeService: PartnerCodeService,
  ) {
  }

  reset(): void {
    this.scanPartnerCodeInfo = null;
    this.logout();
  }

  get enabled(): boolean {
    return this.shopSettingsService.shopSettings.partnerCodeAuthorization &&
      this.shopSettingsService.isVUMode;
  }

  getPartnerInfo(card: ScanCard): Observable<ScanPartnerCodeInfo> {
    return this.partnerCodeService.getPartnerInfo(card);
  }

  registerCard(card: ScanCard, activate = false): Observable<Partner> {
    return this.partnerCodeService.registerCard(card, activate);
  }

  partnerAccountPayment(amountToPay: number): Observable<string> {
    return this.partnerCodeService.partnerAccountPayment(amountToPay);
  }

  register(partner: Partner, partnerCode: string, pin: string): Observable<boolean> {
    return this.partnerCodeService.create(partner, partnerCode, pin).pipe(
      map(newPartner => {
        if (newPartner) {
          return false;
        }
        this._login(newPartner, partnerCode, pin);
        return true;
      })
    );
  }

  login(partner: Partner, partnerCode: string, pin: string): Observable<boolean> {
    return this.partnerCodeService.checkPin(partnerCode, pin).pipe(
      tap(result => {
        if (result) {
          this._login(partner, partnerCode, pin);
        }
      })
    );
  }

  private _login(partner: Partner, partnerCode: string, pin: string): void {
    this._partner = partner;
    this._partnerCode = partnerCode;
    this._pin = pin;
    this.authorizationChanged.next(true);
  }

  logout(): void {
    this._partner = null;
    this._partnerCode = null;
    this._pin = null;
    this.authorizationChanged.next(false);
  }

  get loggedInPartner(): boolean {
    return !!this._partner && !!this._partnerCode;
  }

  get partner(): Partner {
    return this._partner;
  }

  get partnerCode(): string {
    return this._partnerCode;
  }

  get pin(): string {
    return this._pin;
  }

  get accessAllowed(): boolean {
    return !this.enabled || this.loggedInPartner;
  }

  get partnerCodePaymentAllowed(): boolean {
    if (!this.shopSettingsService.shopSettings || !this.shopSettingsService.shopSettings.partnerCodePayment || !this.accessAllowed) {
      return false;
    }

    return true;
  }
}
